import { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  LogOut, Menu as MenuIcon, X, ChevronRight, ChevronDown,
} from 'lucide-react';
import { Toaster } from './Toaster';
import { NodeSwitcher } from './NodeSwitcher';
import { ScopePinToggle } from './ScopePinToggle';
import { NotificationBell } from './NotificationBell';
import { TenantSwitcher } from './TenantSwitcher';
import { SystemAdminBanner } from './SystemAdminBanner';
import { EnvironmentBadge, activeEnvBadge } from './EnvironmentBadge';
import { ReleaseChangelogModal } from './ReleaseChangelogModal';
import { QuotaExceededModal } from './QuotaExceededModal';
import { clearTokens } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import {
  NAV_GROUPS, SIDEBAR_CONFIG_ENTRIES, sidebarItemsOf, canShowNavEntry, type NavEntry,
} from '@/lib/navigation';
import { useRootLabel } from '@/stores/scope';
import { useScopePinStore } from '@/stores/scope-pin';
import { acquireScrollLock, releaseScrollLock } from '@/lib/scroll-lock';
import { APP_VERSION } from '@/lib/version';
import { shouldShowChangelog, markChangelogSeen } from '@/lib/release-changelog';
import { withMaxLen } from '@/lib/dev-maxlen';
import { useLabel } from '@/lib/labels';
import misesparkSymbol from '@/assets/logo/misespark-symbol-dark.svg';

/** MiseSpark ロゴタイプ ("Mise" navy + "Spark" オレンジ→イエローの二色刷り、ロゴ実測値を tailwind.config.js の misespark.navy/orange/yellow に反映) */
function MiseSparkWordmark({ className, onChrome = false }: { className?: string; onChrome?: boolean }) {
  return (
    <span className={className}>
      {/* クローム上では Mise を白にする (紺地にnavyは読めない、§2.1) */}
      <span className={onChrome ? 'text-white' : 'text-misespark-navy'}>Mise</span>
      <span className="bg-gradient-to-r from-misespark-orange to-misespark-yellow bg-clip-text text-transparent">Spark</span>
    </span>
  );
}

/** Lite版テナントのみロゴタイプの右に表示する枠線バッジ (docs/reference のロゴ案 5a 採用、SVG不使用でCSSのみ実装)。
 *  塗りバッジにしない (Sparkの橙と競合し視線がLiteに先に行くため)。紺地クローム専用の暗背景仕様のみ実装。 */
function LiteBadge({ className = '' }: { className?: string }) {
  return (
    <span
      className={`self-center shrink-0 whitespace-nowrap rounded-[5px] border-[1.2px] border-white/55 px-[5px] py-px text-[9.5px] font-bold leading-[1.4] text-white ${className}`}
      aria-label="Liteプラン"
    >
      Lite
    </span>
  );
}

// v0.4 追加要件⑱ D-439: ナビ定義は lib/navigation.ts へ単一ソース化した (§8-55 の発生源を解消)。
// 画面の追加・ゲート変更はすべて lib/navigation.ts で行うこと。

// ⚠️⚠️⚠️ NavList/NavSkeleton/ConfigPinnedItem は元々 AppLayout 関数本体の中で定義されており、
// AppLayout が再レンダリングされるたびに「新しい関数コンポーネント」として作り直されていた。
// React は同じJSX位置でもコンポーネントの型 (関数の参照) が変わると別コンポーネントとみなし
// 配下のDOMを丸ごとアンマウント→再マウントする。その結果、サイドバー絞り込み欄で1文字入力する
// たびに<input>要素そのものが作り直され、フォーカス・IME変換中の状態が毎回破棄されていた
// (production実機で「1文字しか入らない」「もも」の重複」「backspace後に再フォーカスが必要」として発覚)。
// ⇒ コンポーネント本体をモジュールスコープへ切り出し、状態は props で受け渡す。

// §7.5 ナビゲーション (クローム): 紺地 (chrome.base) / アクティブは brand.red の pill / 非アクティブは chrome.text-sub
// v0.3 (S-6.5デザインレビュー反映): items-start + アイコンmt-[2px]にすることで、ラベルが2行に
// 折り返した場合でも(最長表示モード等)アイコンとラベル2行目が重ならない。min-h でタップ領域を確保
// (モバイルは44px、デスクトップは40px、§9)
function navItemClass(isActive: boolean, onChrome: boolean) {
  if (isActive) {
    // アクティブ pill: brand.red + 影で「凸」を演出、赤は 1画面 1つの原則 (§2.2)
    return 'group flex items-start gap-2.5 min-h-[44px] md:min-h-[40px] px-3 py-2.5 rounded-md text-[13.5px] font-bold bg-brand-red text-white shadow-[0_2px_0_rgba(0,0,0,.28)] nav-item-press focus-ring';
  }
  return onChrome
    ? 'group flex items-start gap-2.5 min-h-[44px] md:min-h-[40px] px-3 py-2.5 rounded-md text-[13.5px] font-medium text-chrome-text-sub hover:bg-chrome-raised hover:text-white transition-colors nav-item-press focus-ring'
    : 'group flex items-start gap-2.5 min-h-[44px] md:min-h-[40px] px-3 py-2.5 rounded-md text-[13.5px] font-medium text-text-body hover:bg-surface-sunken transition-colors nav-item-press focus-ring';
}
// v0.3 (S-6.5デザインレビュー反映): 従来は非アクティブ×onChrome時のみ7px角マーカーをアイコンと
// 二重表示しておりラベル幅を圧迫していた。NavItemは必ずiconを持つため、マーカーは削除しアイコン単体にする
// (ui-guidelines.md §7.5の7px角マーカーはアイコン非保持項目向けの指定で本コンポーネントには非該当)。
// ラベルには min-w-0 flex-1 を付与し、flexアイテムの既定 min-width:auto による意図しない
// 右あふれ・アイコンとの重なりを防ぐ (§7.5 bugfix-03)
function navItemContent(Icon: NavEntry['icon'], label: string, isActive: boolean, onChrome: boolean) {
  return (
    <>
      <Icon
        size={isActive ? 16 : 15}
        className={`shrink-0 mt-[2px] ${isActive ? '' : onChrome ? 'text-chrome-text-sub' : 'text-text-sub'}`}
        aria-hidden
      />
      {/* v0.3 (S-6.5): 最長表示モード時は16文字の全角ダミーに差し替え、折り返し時のアイコン重なりを検証可能にする */}
      <span className="min-w-0 flex-1 line-clamp-2 leading-snug">{withMaxLen(label, 16)}</span>
    </>
  );
}

// v0.3 (S-16, Design-C3): effective_permissions到着前のnavGroups skeleton。
// 実項目数の目安 (navGroups.length分のバーグループ) で表示しレイアウトジャンプを抑える
function NavSkeleton() {
  return (
    <nav className="px-3 py-4 space-y-4" aria-busy="true" aria-label="ナビゲーション読み込み中">
      {/* v0.4 章③ Design レビュー反映: 中身が空のグループ (D-91 の「シフト」「勤怠・給与」等、
          将来の章で項目が追加されるまで items:[] のプレースホルダ) は、スケルトン表示でも
          見出しバーだけが浮いて見えるため除外する (実描画の filteredGroups と同じ判断基準) */}
      {NAV_GROUPS.filter((g) => sidebarItemsOf(g).length > 0).map((g) => (
        <div key={g.label} className="space-y-1.5">
          <div className="h-3 w-16 rounded bg-chrome-raised/60 animate-pulse" />
          {g.items.slice(0, 2).map((_, i) => (
            <div key={i} className="h-9 rounded-md bg-chrome-raised/40 animate-pulse" />
          ))}
        </div>
      ))}
    </nav>
  );
}

type FilteredGroup = { key: string; label: string; items: NavEntry[] };

// fix-sidebar-scroll.md 対処3: グループは折りたたみ式 (既定は現在のルートが属するグループのみ展開)。
// 「コンフィグ」はこのスクロール領域には含めず、呼出元 (aside/drawer) で兄弟要素として固定表示する
function NavList({
  onChrome = true,
  filteredGroups,
  isGroupExpanded,
  toggleGroup,
  navFilter,
  setNavFilter,
  navFilterQuery,
  setNavFilterQuery,
  isComposingRef,
}: {
  onChrome?: boolean;
  filteredGroups: FilteredGroup[];
  isGroupExpanded: (key: string) => boolean;
  toggleGroup: (key: string) => void;
  navFilter: string;
  setNavFilter: (v: string) => void;
  navFilterQuery: string;
  setNavFilterQuery: (v: string) => void;
  isComposingRef: React.MutableRefObject<boolean>;
}) {
  const filteredMatches = navFilterQuery
    ? filteredGroups.flatMap((g) => g.items
      .filter((n) => n.label.toLowerCase().includes(navFilterQuery))
      .map((n) => ({ ...n, groupLabel: g.label })))
    : [];

  return (
    <nav className="px-3 py-4 space-y-1">
      <div className="pb-2">
        <label htmlFor={`nav-filter-${onChrome ? 'chrome' : 'plain'}`} className="sr-only">メニューを絞り込む</label>
        <input
          id={`nav-filter-${onChrome ? 'chrome' : 'plain'}`}
          type="search"
          value={navFilter}
          onChange={(e) => {
            const v = e.target.value;
            setNavFilter(v);
            if (!isComposingRef.current) setNavFilterQuery(v.trim().toLowerCase());
          }}
          onCompositionStart={() => { isComposingRef.current = true; }}
          onCompositionEnd={(e) => {
            isComposingRef.current = false;
            setNavFilterQuery(e.currentTarget.value.trim().toLowerCase());
          }}
          placeholder="メニューを絞り込む"
          className={`w-full min-h-[44px] px-3 py-2 text-[13px] rounded-md focus-ring ${
            onChrome
              // ⚠️ 入力文字は chrome.text (白) にすること。当初 chrome.base (#0B2450) を指定して
              //    しまい、chrome.raised (#1B3868) 地に対し **1.31:1** で読めなかった
              //    (ブラウザ実機検証で発覚)。白は 11.59:1、placeholder の text-label は 4.65:1。
              ? 'bg-chrome-raised text-chrome-text placeholder:text-chrome-text-label border border-chrome-marker/40'
              : 'bg-surface-sunken text-text-body placeholder:text-text-sub border border-surface-line'
          }`}
        />
      </div>
      {navFilterQuery && (
        <div className="space-y-1 pb-2" role="region" aria-label="絞り込み結果">
          <p className={`px-3 py-1 text-[10px] font-bold ${onChrome ? 'text-chrome-text-label' : 'text-text-section'}`} aria-live="polite">
            {filteredMatches.length > 0 ? `${filteredMatches.length}件` : '該当する項目がありません'}
          </p>
          {filteredMatches.map(({ to, label, icon: Icon, groupLabel }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/dashboard' || to === '/'}
              onClick={() => { setNavFilter(''); setNavFilterQuery(''); }}
              className={({ isActive }) => navItemClass(isActive, onChrome)}
            >
              {({ isActive }) => navItemContent(Icon, `${label}（${groupLabel}）`, isActive, onChrome)}
            </NavLink>
          ))}
        </div>
      )}
      {!navFilterQuery && filteredGroups.map((group) => {
        // Designレビュー CRITICAL反映 (2026-08-03): 「ホーム」等の単一項目グループはアコーディオン化
        // すると、最頻導線なのに「開く→押す」の2手順になってしまう (Nielsen H8)。1件のみのグループは
        // 折りたたみ見出しを持たせず直接リンクとして表示する
        if (group.items.length === 1) {
          const { to, label, icon: Icon } = group.items[0]!;
          return (
            <div key={group.key} className="pb-1">
              <NavLink to={to} end={to === '/dashboard' || to === '/'} className={({ isActive }) => navItemClass(isActive, onChrome)}>
                {({ isActive }) => navItemContent(Icon, label, isActive, onChrome)}
              </NavLink>
            </div>
          );
        }
        const expanded = isGroupExpanded(group.key);
        const Chevron = expanded ? ChevronDown : ChevronRight;
        return (
          <div key={group.key}>
            <button
              type="button"
              onClick={() => toggleGroup(group.key)}
              aria-expanded={expanded}
              // v0.3 (S-6.5デザインレビュー反映): tracking-[0.1em]/uppercaseは欧文の全大文字見出し向けの
              // 指定で、日本語ラベルに適用すると「マ ス タ」のように文字間が分解して見えるため外す (§4.1)
              className={`w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold tracking-normal rounded-md transition-colors focus-ring ${onChrome ? 'text-chrome-text-label hover:bg-chrome-raised' : 'text-text-section hover:bg-surface-sunken'}`}
            >
              {/* D-U9 (v0.4 使い勝手フィードバック 2026-09-04): min-w-0 flex-1 が無いと、flexアイテムの
                  既定 min-width:auto によりラベルが縮まずボタン外へ意図せずあふれ (§7.5 bugfix-03と同型)、
                  結果として「マイページ」が「マイ」までしか見えない見切れになっていた */}
              <span className="min-w-0 flex-1 truncate text-left">{withMaxLen(group.label, 8)}</span>
              <Chevron size={12} className="shrink-0" aria-hidden />
            </button>
            {expanded && (
              <div className="space-y-1 pb-3">
                {group.items.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/dashboard' || to === '/'}
                    className={({ isActive }) => navItemClass(isActive, onChrome)}
                  >
                    {({ isActive }) => navItemContent(Icon, label, isActive, onChrome)}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

// fix-sidebar-scroll.md 対処3: 「コンフィグ」専用。スクロールするnavの兄弟要素として、
// サイドバー最下部に常時固定表示する
function ConfigPinnedItem({ onChrome = true, configEntry }: { onChrome?: boolean; configEntry: NavEntry | undefined }) {
  if (!configEntry) return null;
  return (
    <div className={`px-3 py-2 border-t ${onChrome ? 'border-chrome-raised' : 'border-surface-line'}`}>
      <NavLink to={configEntry.to} className={({ isActive }) => navItemClass(isActive, onChrome)}>
        {({ isActive }) => navItemContent(configEntry.icon, configEntry.label, isActive, onChrome)}
      </NavLink>
    </div>
  );
}

export function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const rootLabel = useRootLabel();
  const t = useLabel();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const closeDrawerBtnRef = useRef<HTMLButtonElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);

  const isAdmin = user?.roles?.some((r) => r === 'company_admin' || r === 'platform_admin') ?? false;
  // design-and-codeレビュー反映 (2026-08-06): edition==='lite'判定がnavフィルタ+バッジ表示の
  // 計6箇所に分散していたため1変数に集約 (将来'trial'等のedition追加時の修正漏れ防止)
  const isLiteEdition = user?.edition === 'lite';
  // v0.3 (S-16): requiredFeatureが定義されたnav項目はeffective_permissionsベースで判定し、
  // 未定義の項目は従来通りadmin_requiredで判定する (docs/design/v03-custom-roles.md §2.4.2)。
  // QAレビュー Major反映 (2026-08-03): admin_required の可視判定を1箇所に集約する
  // (navGroups側とCONFIG_ITEM側で別々にisAdminを見ると、将来カスタムロール実装時に片方だけ
  // 更新漏れが起きうる)
  const canShowNavItem = (n: NavEntry) => canShowNavEntry(n, user, isAdmin);

  // 5-1 (docs/design/v05-dashboard-scope-pinning.md §0-2-2): 初回ログイン時にテナント別の
  // 固定状態をlocalStorageから復元する。テナント切替時はuseSwitchTenant.ts側で同期的に
  // loadForTenantを呼ぶため、ここは初回マウント相当のケースのみを担う。
  const loadScopePinForTenant = useScopePinStore((s) => s.loadForTenant);
  useEffect(() => {
    if (user?.tenant_id) loadScopePinForTenant(user.tenant_id);
  }, [user?.tenant_id, loadScopePinForTenant]);

  const permsReady = usePermsReady();
  const [showSkeleton, setShowSkeleton] = useState(true);
  useEffect(() => {
    if (!permsReady) return;
    const handle = setTimeout(() => setShowSkeleton(false), 100);
    return () => clearTimeout(handle);
  }, [permsReady]);

  // v0.3 (S-17 Design-H4): 初回ログイン (このユーザーがまだ見ていない最新チェンジログがある場合) に
  // モーダルで新機能を案内する。バックエンド永続化はせずlocalStorageのみで判定する簡易実装
  // (docs/design/v03-custom-roles.md §8g)。Designレビュー HIGH反映: 当初トースト+モーダルを同時
  // 表示していたが、同一内容の二重発信・モバイルでの視覚的重なりを指摘されモーダル単独に一本化した
  const [changelogOpen, setChangelogOpen] = useState(false);
  useEffect(() => {
    if (!user || !shouldShowChangelog(user.user_id)) return;
    setChangelogOpen(true);
  }, [user]);
  const closeChangelog = () => {
    setChangelogOpen(false);
    if (user) markChangelogSeen(user.user_id);
  };
  const filteredGroups = NAV_GROUPS
    .map((g) => ({
      ...g,
      // v0.4 F-1 (D-197b): labelKey が指定されたグループ名はテナント別ラベル override を反映する
      label: g.labelKey ? t(g.labelKey, g.label) : g.label,
      items: sidebarItemsOf(g)
        .filter(canShowNavItem)
        .map((n) => (n.dynamicRootLabel ? { ...n, label: `${rootLabel}サマリ` } : n)),
    }))
    .filter((g) => g.items.length > 0);

  // fix-sidebar-scroll.md 対処3: サイドバーを折りたたみ式にし、既定では現在のルートが属する
  // グループのみ展開する。ユーザーが明示的に開閉した場合はその選択をlocalStorageに保持する
  // v0.4 追加要件⑱ Q-294 (D-442 と同時): 開閉状態のキーを**グループのラベルから安定キー (group.key) へ移した**。
  // ⚠️ ラベルは D-442 のグループ再編で変わり、`tenant_labels` によるテナント別 override でも変わる。
  //    ラベルをキーにしていると、**改名やテナント設定のたびに利用者の開閉状態が黙って失効する**。
  // ⚠️ 移行時の一度きりの影響: 旧キー (ラベル) で保存された値は読み捨てられ、初回だけ
  //    「現在地のグループのみ展開」に戻る。現在地は必ず展開される実装のため実害はない。
  const activeGroupKey = filteredGroups.find((g) =>
    g.items.some((item) => (item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to))),
  )?.key;
  const [manualExpanded, setManualExpanded] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('nav-group-expanded');
      if (!saved) return {};
      const parsed: unknown = JSON.parse(saved);
      // QAレビュー CRITICAL反映 (2026-08-03): JSON.parse('null') は例外を投げずnullを返すため、
      // 破損値 (文字列"null"等) がtry/catchを素通りしてサイドバー全体がクラッシュしていた。
      // パース結果が実際にプレーンオブジェクトであることまで検証する
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, boolean> : {};
    } catch {
      return {};
    }
  });
  // Designレビュー CRITICAL反映 (2026-08-03): 現在のルートが属するグループは、過去に手動で
  // 閉じた履歴(localStorage)があっても必ず展開する。そうしないと「別グループを閉じた後、
  // 再度そのグループのページへ来ても展開されない」= 現在地のナビ項目がサイドバーから消える
  // (Nielsen H1 違反)。非アクティブなグループの開閉のみユーザーの選択に従う
  const isGroupExpanded = (key: string) => key === activeGroupKey || (manualExpanded[key] ?? false);
  const toggleGroup = (key: string) => {
    setManualExpanded((prev) => {
      const next = { ...prev, [key]: !isGroupExpanded(key) };
      try { localStorage.setItem('nav-group-expanded', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  // fix-sidebar-scroll.md 対処2: ルート変更時はコンテンツ領域だけを先頭へ戻す
  // (サイドバー側のスクロール位置は保持し、続けて近くの項目を操作できるようにする)
  useEffect(() => { if (mainRef.current) mainRef.current.scrollTop = 0; }, [location.pathname]);
  useEffect(() => {
    if (!drawerOpen) return;
    acquireScrollLock();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    queueMicrotask(() => closeDrawerBtnRef.current?.focus());
    return () => {
      document.removeEventListener('keydown', onKey);
      releaseScrollLock();
      hamburgerRef.current?.focus();
    };
  }, [drawerOpen]);

  const onLogout = () => {
    clearTokens();
    setUser(null);
    // 別テナントアカウントでの再ログイン時に['nodes']等テナント非依存queryKeyの古いキャッシュが
    // 残り、ヘッダー等に前アカウントのテナント情報が表示され続ける不具合対策 (useSwitchTenant.tsと同型)
    queryClient.cancelQueries();
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  // v0.4 追加要件⑱ D-444: サイドバーの絞り込み。
  // ⚠️ **グループ設計の完全性への依存を下げるのが目的**。⑬〜⑲ で項目はさらに増える見込みであり、
  //    「完璧な分類」を追い続けるより、名前で辿り着ける手段を1つ持つ方が持続する。
  // ⚠️ 入力中は**グループの開閉状態を無視して一致項目だけを平坦に出す** (開いて探す手間をなくす)。
  const [navFilter, setNavFilter] = useState('');
  // ⚠️ IME変換確定前 (isComposing中) はnavFilterQueryを更新しない。素直にnavFilterから毎回
  // 導出すると、日本語入力の変換中ローマ字断片でも即座に絞り込みが走り「該当なし」が一瞬出る
  // (production実機で報告)。navFilterはinputのvalueとして毎キー更新する必要があるため
  // (でないとIME変換候補が出ない)、フィルタ実行用のクエリだけ確定タイミングを分離する。
  const [navFilterQuery, setNavFilterQuery] = useState('');
  const isComposingRef = useRef(false);

  // fix-sidebar-scroll.md 対処3: 「コンフィグ」専用。スクロールするnavの兄弟要素として、
  // サイドバー最下部に常時固定表示する (admin_required のため isAdmin チェックのみ行う)
  const configEntry = SIDEBAR_CONFIG_ENTRIES.find(canShowNavItem);

  return (
    // §7.5 クローム: ヘッダー・サイドバーは chrome.base の連続した紺面
    // fix-sidebar-scroll.md 対処1: ドキュメントスクロールを無くし、サイドバーとコンテンツを
    // それぞれ独立したスクロール領域にする (h-[100dvh] はiOS Safariのアドレスバー分のズレ対策)
    <div className="h-screen h-[100dvh] overflow-hidden flex flex-col bg-chrome-base">
      <Toaster />
      <ReleaseChangelogModal open={changelogOpen} onClose={closeChangelog} />
      <QuotaExceededModal />
      {/* システム管理者バナー + 環境(staging/demo)バナー + ヘッダー */}
      <div className="sticky top-0 z-30">
        <SystemAdminBanner />
        {activeEnvBadge && <div className={`h-1.5 w-full ${activeEnvBadge.stripClass} shrink-0`} />}
        <header className="h-14 bg-chrome-base flex items-center justify-between gap-2 px-3 sm:px-6">
          <div className="flex items-center gap-2 min-w-0">
            <button
              ref={hamburgerRef}
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-md text-chrome-text-sub hover:bg-chrome-raised hover:text-white focus-ring"
              aria-label="メニューを開く"
              aria-expanded={drawerOpen}
              aria-controls="mobile-nav-drawer"
            >
              <MenuIcon size={20} />
            </button>
            <img src={misesparkSymbol} alt="" className="w-8 h-8 shrink-0" />
            {/* ⚠️ 430px以下ではロゴタイプを隠しシンボルのみにする。ヘッダーは
                「左(ロゴ) / 中央(NodeSwitcher) / 右(通知・ログアウト)」の3ブロックだが、
                中央は flex-1 basis-0 のため左ブロックを縮ませる力を持たない。狭幅で店舗名が
                入らないと中央の中身が justify-center のまま左右へはみ出し、ロゴに重なる
                (2026-09-10 実測: 320pxで42px・390pxで7px重なり、§8-189)。
                NodeSwitcher 側の min-w-0/w-full と対で、どの幅でも重ならないようにしている */}
            <MiseSparkWordmark className="text-lg font-extrabold tracking-tight truncate max-[430px]:hidden" onChrome />
            {isLiteEdition && <LiteBadge />}
            {/* 狭幅ではLiteバッジ・環境ピルが中央のNodeSwitcherと競合し圧迫するため、環境ピルはsm以上でのみ表示する
                (モバイルではヘッダー直上の帯 <activeEnvBadge && <div className="h-1.5 ..."> だけで判別可能) */}
            <span className="hidden sm:inline-flex">
              <EnvironmentBadge variant="header" />
            </span>
          </div>
          <div className="flex-1 flex items-center justify-center gap-1 min-w-0 px-1">
            <NodeSwitcher />
            <ScopePinToggle />
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <TenantSwitcher />
            {user && <NotificationBell />}
            {user && (
              <Link
                to="/profile"
                className="hidden sm:inline text-sm text-chrome-text-sub hover:text-white transition-colors"
                title="プロフィールを開く"
              >
                {user.name} さん
              </Link>
            )}
            <button
              onClick={onLogout}
              type="button"
              className="flex items-center gap-1 text-sm text-chrome-text-sub hover:text-white transition-colors focus-ring rounded-md px-1 py-1"
              aria-label="ログアウト"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">ログアウト</span>
            </button>
          </div>
        </header>
      </div>

      <div className="flex-1 min-h-0 flex">
        {/* サイドバー (デスクトップ) — chrome.base、ヘッダーとの境界線は引かない (§7.5)。
            navの部分だけが独立してスクロールし、コンフィグ・バージョン表記は常時固定表示 */}
        <aside className="w-[200px] bg-chrome-base hidden md:flex md:flex-col shrink-0">
          <div className="flex-1 min-h-0 overflow-y-auto">
            {!permsReady || showSkeleton ? <NavSkeleton /> : (
                  <NavList
                    onChrome
                    filteredGroups={filteredGroups}
                    isGroupExpanded={isGroupExpanded}
                    toggleGroup={toggleGroup}
                    navFilter={navFilter}
                    setNavFilter={setNavFilter}
                    navFilterQuery={navFilterQuery}
                    setNavFilterQuery={setNavFilterQuery}
                    isComposingRef={isComposingRef}
                  />
                )}
          </div>
          <ConfigPinnedItem onChrome configEntry={configEntry} />
          <div className="px-4 py-2 text-[11px] text-chrome-text-label tabular-nums flex items-center flex-wrap gap-1">
            <MiseSparkWordmark onChrome />
            {isLiteEdition && <LiteBadge className="ml-0.5" />}
            <span>{APP_VERSION}</span>
          </div>
        </aside>

        {/* ドロワー (モバイル) — 紺のまま L3 影で浮かせる */}
        {drawerOpen && (
          <div id="mobile-nav-drawer" className="md:hidden fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="ナビゲーション">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setDrawerOpen(false)}
            />
            <aside className="absolute left-0 top-0 h-full w-64 max-w-[80%] bg-chrome-base shadow-overlay flex flex-col">
              <div className="flex items-center justify-between px-4 h-14 bg-chrome-base">
                <div className="flex items-center gap-2 min-w-0">
                  <img src={misesparkSymbol} alt="" className="w-8 h-8 shrink-0" />
                  <MiseSparkWordmark className="text-lg font-extrabold tracking-tight truncate" onChrome />
                  {isLiteEdition && <LiteBadge />}
                </div>
                <button
                  ref={closeDrawerBtnRef}
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="p-2 rounded-md text-chrome-text-sub hover:bg-chrome-raised hover:text-white focus-ring"
                  aria-label="メニューを閉じる"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {!permsReady || showSkeleton ? <NavSkeleton /> : (
                  <NavList
                    onChrome
                    filteredGroups={filteredGroups}
                    isGroupExpanded={isGroupExpanded}
                    toggleGroup={toggleGroup}
                    navFilter={navFilter}
                    setNavFilter={setNavFilter}
                    navFilterQuery={navFilterQuery}
                    setNavFilterQuery={setNavFilterQuery}
                    isComposingRef={isComposingRef}
                  />
                )}
                {user && (
                  <div className="px-3 py-2 mt-2 border-t border-chrome-raised">
                    <Link
                      to="/profile"
                      className="block px-3 py-2 rounded-md text-sm text-chrome-text-sub hover:bg-chrome-raised hover:text-white"
                    >
                      {user.name} さん（プロフィール）
                    </Link>
                  </div>
                )}
              </div>
              <ConfigPinnedItem onChrome configEntry={configEntry} />
              <div className="px-4 py-2 text-[11px] text-chrome-text-label tabular-nums flex items-center flex-wrap gap-1">
                <MiseSparkWordmark onChrome />
                {isLiteEdition && <LiteBadge className="ml-0.5" />}
                <span>{APP_VERSION}</span>
              </div>
            </aside>
          </div>
        )}

        {/* 作業面: クロームの内側に 12px 余白 + radius.xl の白いパネル (§7.5)。
            独立したスクロール領域 (fix-sidebar-scroll.md 対処1)、ルート変更時に mainRef で先頭へ戻す (対処2)。
            2026-09-09: 高速スクロール時にカードの box-shadow (shadow-card) 部分が一瞬白く抜ける
            描画ラグを、will-change-transform でこの要素を独立したコンポジットレイヤーに昇格させて解消 */}
        <main ref={mainRef} className="flex-1 min-w-0 overflow-y-auto bg-surface-canvas will-change-transform">
          <div className="p-2 sm:p-3 min-h-full">
            <div className="bg-surface-canvas rounded-xl shadow-card min-h-[calc(100vh-56px-16px)] p-4 sm:p-6 lg:p-[26px]">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
